import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { PERMISSIONS } from '../../../config/constants';
import { UPDATE_PROPOSAL_LOGO } from '../../../graphql/proposal/mutation';
import { useNotification } from '../../../hooks/useNotification';
import { usePermissions } from '../../../hooks/usePermissions';
import type { ProposalData } from '../../../types/proposal';
import type { SingleProposalOutletContext } from '../../../views/SingleProposal';
import { CampaignInfluencers } from '../../campaigns/Sidebar/CampaignInfluencers';
import { CampaignPrice } from '../../campaigns/Sidebar/CampaignPrice';
import { HiredPlatforms } from '../../campaigns/Sidebar/HiredPlatforms';
import { Button } from '../../common/Button';
import FileUploader from '../../common/FileUploader';
import { BrandImage } from '../../images/BrandImage';
import { BrandGroupDropdown } from '../../inputs/BrandGroupDropdown';
import { DatePickerInput } from '../../inputs/DatePicker';
import { GenericDropdown } from '../../inputs/GenericDropdown';
import { MoneyCurrencyDropdown } from '../../inputs/MoneyCurrencyDropdown';
import { MoneyInput } from '../../inputs/MoneyInput';
import ProposalStatusDropdown from '../../inputs/ProposalStatusDropdown';
import { TextInput } from '../../inputs/TextInput';
import { UserDropdown } from '../../inputs/UserDropdown';
import { UserDropdownList } from '../../inputs/UserDropdownList';

type TCampaignSettingsForm = {
  name: string;
  brand_id: string;
  group_id: string;
  campaign_type: 'one_time' | 'recurring';
  month?: string;
  money: number | null;
  currency: string;
  influencers: { value: string }[];
  accountManagers: { value: string }[];
};

function ProposalSidebarCard({
  proposal,
  refetch,
}: {
  proposal: ProposalData;
  refetch: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="bg-dark-blue 2xl:p-5 p-4 flex flex-col gap-4 rounded-10 lg:w-[420px] flex-none h-fit">
      <div className="bg-influentia-black rounded-10 p-4 flex flex-row gap-4 items-center">
        <BrandImage
          image={proposal.brand_profile_picture || undefined}
          size="size-[76px]"
        />
        <div className="flex flex-col gap-1">
          <span className="text-white font-bold">
            {proposal.brand_name || '-'}
          </span>
          <span className="text-gray-400 text-sm">{proposal.month || '-'}</span>
          <ProposalStatusDropdown
            defaultStatus={proposal.status}
            proposalId={proposal.id}
            refetch={refetch}
          />
        </div>
      </div>
      <CampaignPrice
        price={proposal.money ?? 0}
        currency={proposal.currency ?? undefined}
      />
      <HiredPlatforms
        platforms={Array.from(
          new Set(
            proposal.ProposalUsers.flatMap((u) =>
              (u.actions ?? []).map((a) => a.platform),
            ).filter(Boolean),
          ),
        )}
      />
      <CampaignInfluencers
        influencers={proposal.ProposalUsers.map((u) => ({
          id: u.id,
          name: u.name || u.id,
        }))}
      />
      {proposal.accountManagers && proposal.accountManagers.length > 0 && (
        <div className="flex flex-row gap-[6px]">
          <div className="rounded-full bg-white flex justify-center items-center size-[28px] text-influentia-black flex-none">
            <svg
              className="size-4"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-influentia-light-blue font-medium text-[10px]">
              {t('proposals.fields.accountManagers')}
            </span>
            <div className="flex flex-col gap-1">
              {proposal.accountManagers.map((am) => (
                <span key={am.id} className="font-medium text-white text-sm">
                  {am.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SingleProposalCampaignSettingsTab() {
  const { t } = useTranslation('common');
  const { checkPermission } = usePermissions();
  const { notifyWarning, notifySaveSuccess } = useNotification();

  const { proposal, updateProposal, refetch, updating } =
    useOutletContext<SingleProposalOutletContext>();

  const [updateProposalLogo] = useMutation(UPDATE_PROPOSAL_LOGO);

  const uploadLogo = (file: File) => {
    updateProposalLogo({
      variables: { id: proposal.id, file },
      context: {
        headers: {
          'content-type': 'multipart/form-data',
          'x-apollo-operation-name': 'UpdateProposalLogo',
        },
      },
      onCompleted: () => {
        notifySaveSuccess();
        refetch();
      },
      onError: () => notifyWarning(t('proposals.approveError')),
    });
  };

  const campaignMethods = useForm<TCampaignSettingsForm>({
    values: proposal
      ? {
          name: proposal.name,
          brand_id: proposal.brand_id || '',
          group_id: proposal.group_id || '',
          campaign_type: proposal.campaign_type || 'one_time',
          month: proposal.month
            ? dayjs(proposal.month, 'MM/YYYY').format('MM/DD/YYYY')
            : '',
          money: proposal.money,
          currency: proposal.currency || '€',
          influencers: proposal.ProposalUsers.map((u) => ({
            value: u.user_id || u.id,
          })),
          accountManagers: (proposal.accountManagers ?? []).map((am) => ({
            value: am.id,
          })),
        }
      : undefined,
  });

  const {
    control: ctrlCampaign,
    watch,
    handleSubmit: handleCampaignSubmit,
    formState: { errors: campaignErrors },
    register: regCampaign,
  } = campaignMethods;

  const brandId = watch('brand_id');

  const onSaveCampaign = (formData: TCampaignSettingsForm) => {
    const accountManagerIds = formData.accountManagers
      .map((am) => am.value)
      .filter(Boolean);

    const influencerIds = formData.influencers
      .map((inf) => inf.value)
      .filter(Boolean);

    const moneyToSend =
      formData.money !== null && formData.money !== undefined
        ? parseFloat(String(formData.money))
        : undefined;

    updateProposal({
      variables: {
        id: proposal.id,
        name: formData.name,
        brand_id: formData.brand_id || undefined,
        group_id: formData.group_id || undefined,
        month:
          formData.campaign_type === 'one_time' && formData.month
            ? dayjs(formData.month).format('MM/YYYY')
            : undefined,
        money: moneyToSend,
        currency: formData.currency || undefined,
        account_manager_ids: accountManagerIds,
        campaign_type: formData.campaign_type,
        influencer_ids: influencerIds,
      },
      onCompleted: () => {
        notifySaveSuccess();
        refetch();
      },
      onError: () => notifyWarning(t('proposals.approveError')),
    });
  };

  return (
    <FormProvider {...campaignMethods}>
      <form
        onSubmit={handleCampaignSubmit(onSaveCampaign)}
        className="flex flex-col gap-y-5"
      >
        {/* Top section: logo (left) + fields (right) */}
        <div className="flex flex-row gap-x-5 items-start">
          {/* LEFT: Logo uploader */}
          <div className="flex flex-col gap-2 md:w-[300px] xl:w-[500px] h-full flex-none">
            <span className="text-[16px] uppercase">
              {t('proposals.fields.logo')}
            </span>
            {proposal.logo_url && (
              <div className="mb-2 flex justify-center">
                <img
                  src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${proposal.logo_url}`}
                  className="w-full md:max-h-[200px] object-contain"
                  alt="logo"
                />
              </div>
            )}
            <FileUploader
              submitFile={uploadLogo}
              autoSubmit
              maxSizeMb={100}
              acceptTypes={['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp']}
              dropLabel={t('proposals.fields.logoDropLabel')}
            />
          </div>

          {/* RIGHT: Form fields */}
          <div className="flex-1 flex flex-col gap-4">
            <TextInput
              register={regCampaign}
              registerName="name"
              type="text"
              label={t('proposals.createName')}
              placeholder={t('proposals.createNamePlaceholder')}
              validation={{ required: t('proposals.nameRequired') }}
              errors={campaignErrors.name}
            />

            <div className="flex lg:flex-row flex-col gap-5">
              <UserDropdown
                control={ctrlCampaign}
                name="brand_id"
                label={t('proposals.fields.brand')}
                placeholder={t('proposals.createClientPlaceholder')}
                type="brand"
                searchable
                fullWidth
                includeProspects
              />
              {watch('campaign_type') === 'one_time' && (
                <DatePickerInput
                  name="month"
                  monthYearPicker
                  isClearable
                  label={t('singleCampaign.campaignMonth')}
                  control={ctrlCampaign}
                  fullSpace
                />
              )}
            </div>

            <BrandGroupDropdown
              brand_id={brandId}
              label={t('proposals.fields.group')}
              name="group_id"
              control={ctrlCampaign}
              isClearable={true}
              placeholder={t('proposals.fields.groupPlaceholder')}
            />

            <div className="flex lg:flex-row flex-col gap-5">
              {watch('campaign_type') === 'recurring' ? (
                <MoneyCurrencyDropdown
                  control={ctrlCampaign}
                  name="currency"
                  label={t('proposals.fields.currency')}
                  joinComponent={false}
                  isClearable={false}
                  options={['€', '$'].map((c) => ({ value: c, label: c }))}
                />
              ) : (
                <MoneyInput
                  fullWidth
                  label={t('proposals.fields.amount')}
                  name="money"
                  currentCurrency={watch('currency')}
                  control={ctrlCampaign}
                  currencySelect={true}
                  registerName="currency"
                />
              )}

              <GenericDropdown
                className="w-full"
                control={ctrlCampaign}
                name="campaign_type"
                label="Tipo de campaña"
                isClearable={false}
                options={[
                  { value: 'one_time', label: 'One Time' },
                  { value: 'recurring', label: 'Recurrente' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Influencers */}
        <UserDropdownList
          type="influencer"
          accessName="influencers"
          label={t('proposals.createInfluencers')}
          accessKey="value"
          addText={t('proposals.createInfluencersAdd')}
          placeholderSelects={t('proposals.createInfluencersPlaceholder')}
          control={ctrlCampaign}
          minLength={0}
          showPptName
          trashIconClassname="fill-current text-red-status hover:text-influentia-light-blue"
          buttonClassname="hover:text-influentia-light-blue"
        />

        {/* Account Managers */}
        <UserDropdownList
          type="influentia"
          accessName="accountManagers"
          label={t('proposals.fields.accountManagers')}
          accessKey="value"
          addText={t('proposals.fields.addAccountManager')}
          placeholderSelects={t('proposals.fields.accountManagerPlaceholder')}
          control={ctrlCampaign}
          minLength={0}
          trashIconClassname="fill-current text-red-status hover:text-influentia-light-blue"
          buttonClassname="hover:text-influentia-light-blue"
        />

        {checkPermission(PERMISSIONS.UPDATE_PROPOSAL) && (
          <div className="flex flex-row items-center gap-4 mt-4">
            <Button
              id="saveCampaignSettings"
              style="light-blue"
              text={t('global.save')}
              customClassnames="px-16"
              type="submit"
              loading={updating}
            />
          </div>
        )}
      </form>
    </FormProvider>
  );
}
